import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus } from "lucide-react";
import { useLocation, useSearch } from "wouter";
import {
  applyCertificationDeepLinkToSearch,
  parseCertificationDeepLink,
  removeCertificationDeepLinkFromSearch,
} from "@/lib/certificationDeepLink";
import { CertificationCreateDialog } from "./CertificationCreateDialog";
import { CertificationDetailDialog } from "./CertificationDetailDialog";

function money(v: string | number | null | undefined, currency = "EUR"): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("es-ES", { style: "currency", currency });
}

const STRATEGY_LABELS: Record<string, string> = {
  milestones: "Hitos",
  periodic_certifications: "Certificaciones periódicas",
  mixed: "Mixta",
  single_invoice: "Factura única",
  on_demand: "A demanda",
  non_billable: "No facturable",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  pending_pm_review: "Pendiente revisión PM",
  pm_approved: "Aprobado PM",
  sent_to_client: "Enviado al cliente",
  client_accepted: "Aceptado por cliente",
  rejected: "Rechazado",
  cancelled: "Cancelado",
};

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  if (status === "client_accepted") return "default";
  if (status === "rejected" || status === "cancelled") return "destructive";
  if (status === "draft") return "secondary";
  return "outline";
}

interface Props {
  contractId: number;
}

export function CertificationsSection({ contractId }: Props) {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const certificationDeepLink = parseCertificationDeepLink(search);
  const detailId = certificationDeepLink.certificationId;
  const utils = trpc.useUtils();
  const capsQuery = trpc.contractCertificationsGetMyCapabilities.useQuery();
  const caps = capsQuery.data;

  const economicCtxQuery = trpc.contractCertificationsEconomicContext.useQuery(
    { contractId },
    { enabled: !!contractId && (caps?.canReadEconomics ?? false), retry: false },
  );

  const listQuery = trpc.contractCertificationsList.useQuery(
    { contractId },
    { enabled: !!contractId && (caps?.canRead ?? false) },
  );

  const [createOpen, setCreateOpen] = useState(false);

  const openCertification = (certificationId: number) => {
    const nextSearch = applyCertificationDeepLinkToSearch(search, certificationId);
    setLocation(`${window.location.pathname}${nextSearch}`);
  };

  const closeCertification = () => {
    const nextSearch = removeCertificationDeepLinkFromSearch(search);
    setLocation(`${window.location.pathname}${nextSearch}`, { replace: true });
  };

  if (capsQuery.isLoading) {
    return <div className="space-y-3"><Skeleton className="h-24 w-full" /></div>;
  }

  if (!caps?.canRead) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          No tienes permiso para ver las certificaciones de este contrato.
        </CardContent>
      </Card>
    );
  }

  const ctx = economicCtxQuery.data;
  const strategy = ctx?.billingStrategy;
  const certificationsApply = strategy === "periodic_certifications" || strategy === "mixed";

  if (economicCtxQuery.isFetched && !economicCtxQuery.isError && !certificationsApply && !certificationDeepLink.present) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Certificaciones</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Este contrato no usa una estrategia de certificación periódica
          {strategy ? ` (estrategia actual: ${STRATEGY_LABELS[strategy] ?? "Estrategia no registrada"})` : ""}.
        </CardContent>
      </Card>
    );
  }

  const certList = listQuery.data ?? [];
  const currency = ctx?.currency ?? "EUR";

  return (
    <div className="space-y-3">
      {/* Economic context header */}
      {caps.canReadEconomics && ctx && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Economía de certificaciones</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div>
              <p className="text-muted-foreground text-xs">Techo efectivo</p>
              <p className="font-medium">{money(ctx.ceiling, currency)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Base certificada aceptada (sin IVA)</p>
              <p className="font-medium">{money(ctx.totalAcceptedGross, currency)}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Disponible</p>
              <p className={`font-medium ${Number(ctx.availableCeiling) < 0 ? "text-destructive" : ""}`}>
                {money(ctx.availableCeiling, currency)}
              </p>
            </div>
            {ctx.certificationAllocationPercentage != null && (
              <div>
                <p className="text-muted-foreground text-xs">Asignación certificación</p>
                <p>{ctx.certificationAllocationPercentage}%</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {caps.canReadEconomics && economicCtxQuery.isError && (
        <p role="alert" className="text-sm text-destructive">No se pudo cargar la estrategia de certificaciones del contrato.</p>
      )}

      {/* List */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Certificaciones{certList.length > 0 ? ` (${certList.length})` : ""}
          </CardTitle>
          {caps.canCreate && (
            <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Nueva
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {certificationDeepLink.invalid && (
            <div role="alert" className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded border border-destructive/40 p-3 text-sm text-destructive">
              <span>El enlace a la certificación no contiene un identificador válido.</span>
              <Button size="sm" variant="outline" onClick={closeCertification}>Cerrar enlace</Button>
            </div>
          )}
          {listQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : listQuery.isError ? (
            <p role="alert" className="py-6 text-center text-sm text-destructive">
              No se pudieron cargar las certificaciones de este contrato.
            </p>
          ) : certList.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No hay certificaciones registradas para este contrato.
            </p>
          ) : (
            <div className="space-y-2">
              {certList.map((cert: any) => (
                <button
                  key={cert.id}
                  className="w-full text-left rounded border p-3 hover:bg-muted/50 transition-colors"
                  onClick={() => openCertification(Number(cert.id))}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-sm shrink-0">#{cert.certificationNumber}</span>
                      <span className="text-muted-foreground text-xs truncate">
                        {cert.periodStart} — {cert.periodEnd}
                      </span>
                    </div>
                    <Badge variant={statusVariant(cert.status)} className="shrink-0 text-xs">
                      {STATUS_LABELS[cert.status] ?? "Estado registrado"}
                    </Badge>
                  </div>
                  <div className="mt-1 flex gap-4 text-xs text-muted-foreground">
                    {cert.certificationEconomicReviewRequired === true ? (
                      <span role="alert" className="text-destructive">Revisión económica requerida · importes no disponibles</span>
                    ) : (
                      <>
                        <span>Base certificada sin IVA: <span className="text-foreground font-medium">{money(cert.grossCertifiedAmount, currency)}</span></span>
                        <span>Líquido tras retención: <span className="text-foreground">{money(cert.revisionPayableAmount ?? cert.netCertifiedAmount, currency)}</span></span>
                        {cert.cumulativeCertifiedAmount != null && (
                          <span>Acum.: {money(cert.cumulativeCertifiedAmount, currency)}</span>
                        )}
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CertificationCreateDialog
        contractId={contractId}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(createdId) => {
          setCreateOpen(false);
          openCertification(createdId);
          utils.contractCertificationsList.invalidate({ contractId });
        }}
      />

      {detailId != null && (
        <CertificationDetailDialog
          certificationId={detailId}
          contractId={contractId}
          currency={currency}
          caps={{
            canSubmit: caps.canSubmit ?? false,
            canPmReview: caps.canPmReview ?? false,
            canSendToClient: caps.canSendToClient ?? false,
            canClientAccept: caps.canClientAccept ?? false,
            canCancel: caps.canCancel ?? false,
            canEditDraft: caps.canEditDraft ?? false,
            canReadEconomics: caps.canReadEconomics ?? false,
            canManageDocs: caps.canManageDocs ?? false,
            canManageAccounting: caps.canManageAccounting ?? false,
          }}
          open={detailId != null}
          onClose={closeCertification}
          onMutated={() => {
            utils.contractCertificationsList.invalidate({ contractId });
            utils.contractCertificationsEconomicContext.invalidate({ contractId });
          }}
        />
      )}
    </div>
  );
}
